import { useParams, Link } from "react-router-dom";
import { MapPin, Clock, ArrowLeft, Mail } from "lucide-react";

const jobs: Record<string, {
  title: string;
  location: string;
  type: string;
  department: string;
  about: string;
  responsibilities: string[];
  requirements: string[];
  benefits: string[];
}> = {
  "lifestyle-manager": {
    title: "Lifestyle Manager",
    location: "Abuja",
    type: "Full-time",
    department: "Client Services",
    about:
      "As a Lifestyle Manager at Raha-ídúù, you are the primary point of service for a select portfolio of high-net-worth clients. You anticipate needs before they are expressed, coordinate seamlessly across our partner network, and ensure every experience we deliver is exceptional — without exception.",
    responsibilities: [
      "Manage the day-to-day lifestyle requirements of an assigned client portfolio across travel, dining, accommodation, and personal services",
      "Proactively research and recommend experiences aligned to each client's preferences and lifestyle",
      "Coordinate with Raha-ídúù's partner network — aviation, hospitality, culinary, wellness — to arrange bespoke services",
      "Maintain meticulous records of client preferences, upcoming events, and service history",
      "Handle time-sensitive requests with composure and precision, including out-of-hours situations",
      "Build long-term, trust-based relationships that position Raha-ídúù as an indispensable partner in our clients' lives",
    ],
    requirements: [
      "Minimum 3 years' experience in luxury hospitality, personal assistance, or concierge services",
      "Exceptional written and verbal communication skills in English; additional languages are an advantage",
      "Deep familiarity with Abuja's luxury landscape — hotels, restaurants, private venues, and service providers",
      "Demonstrable ability to handle confidential information with complete discretion",
      "Strong organisational skills and ability to manage multiple high-priority requests simultaneously",
      "A genuine passion for delivering excellence and exceeding expectations",
    ],
    benefits: [
      "Competitive salary commensurate with experience",
      "Performance-based incentives",
      "Access to Raha-ídúù's partner network for personal enrichment",
      "Professional development and training in luxury service standards",
      "A culture built on discretion, excellence, and respect",
    ],
  },
  "events-coordinator": {
    title: "Events Coordinator",
    location: "Lagos",
    type: "Full-time",
    department: "Events",
    about:
      "The Events Coordinator is responsible for designing and executing private events that exceed expectation at every turn. From intimate dinners to exclusive corporate gatherings, you bring creative vision and operational precision together to produce experiences that our clients remember for a lifetime.",
    responsibilities: [
      "Plan and manage all aspects of private events including concept development, venue selection, vendor sourcing, and on-the-day coordination",
      "Liaise with clients to understand their vision and translate it into a detailed, executable brief",
      "Build and maintain relationships with premium venues, caterers, florists, entertainment providers, and other event specialists across Lagos",
      "Manage event budgets and timelines with rigorous attention to detail",
      "Oversee on-site delivery, coordinating all suppliers and staff to ensure a seamless experience",
      "Conduct post-event reviews to capture learnings and continuously raise the bar",
    ],
    requirements: [
      "Minimum 3 years' experience in luxury or high-end event management",
      "Proven track record of delivering flawless private events — portfolio or references required",
      "In-depth knowledge of Lagos's premium event landscape, venues, and supplier ecosystem",
      "Strong project management skills; ability to hold multiple events in planning simultaneously",
      "Creative eye for design, styling, and experiential detail",
      "Calm under pressure, decisive, and solutions-focused",
    ],
    benefits: [
      "Competitive salary commensurate with experience",
      "Performance-based incentives tied to event delivery",
      "Exposure to Lagos's most prestigious private events",
      "Professional development opportunities",
      "Collaborative, high-performance team environment",
    ],
  },
  "client-relations-associate": {
    title: "Client Relations Associate",
    location: "Calabar",
    type: "Full-time",
    department: "Client Services",
    about:
      "The Client Relations Associate is often the first voice our clients hear and the last impression they carry. You are warm, polished, and deeply attentive — someone who makes every client feel as though they are the only client. Based in Calabar, you will support the onboarding of new members and ensure existing clients are consistently delighted.",
    responsibilities: [
      "Serve as the primary contact for new client enquiries — responding promptly, warmly, and professionally across phone and email",
      "Lead the onboarding journey for new members, guiding them through Raha-ídúù's service offering and gathering the information needed to personalise their experience",
      "Maintain accurate, up-to-date client profiles in the CRM system, capturing preferences, feedback, and interaction history",
      "Proactively follow up with clients after service delivery to gather feedback and identify opportunities to improve",
      "Support the Lifestyle Manager team with coordination tasks during peak demand periods",
      "Represent Raha-ídúù's values of discretion, excellence, and personalisation in every interaction",
    ],
    requirements: [
      "Minimum 2 years' experience in a client-facing role within hospitality, luxury retail, or premium services",
      "Exceptionally polished written and verbal communication — your words reflect our brand",
      "Natural empathy and the ability to build rapport quickly while maintaining professional boundaries",
      "Proficiency with CRM systems or a demonstrated ability to learn new tools quickly",
      "High level of organisation and follow-through — nothing falls through the cracks",
      "Genuine enthusiasm for luxury lifestyle and the Calabar region",
    ],
    benefits: [
      "Competitive salary commensurate with experience",
      "Clear progression path within the Raha-ídúù client services team",
      "Training in luxury service standards and client relationship management",
      "A supportive, close-knit team in a growing brand",
      "Performance incentives based on client satisfaction metrics",
    ],
  },
};

export default function JobPage() {
  const { slug } = useParams<{ slug: string }>();
  const job = slug ? jobs[slug] : null;

  if (!job) {
    return (
      <main style={{ backgroundColor: "#0b1a14" }} className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-gray-400 mb-6">Role not found.</p>
          <Link to="/about#careers" className="text-[#C4A77D] text-sm underline-offset-4 hover:text-white transition-colors">
            View all open roles
          </Link>
        </div>
      </main>
    );
  }

  const applySubject = encodeURIComponent(`Application — ${job.title}, ${job.location}`);
  const applyBody = encodeURIComponent(
    `Dear Raha-ídúù Team,\n\nI am writing to apply for the ${job.title} role based in ${job.location}.\n\n[Please attach your CV and a brief note about yourself.]\n\nThank you.`
  );

  return (
    <main style={{ backgroundColor: "#0b1a14" }}>
      {/* Hero */}
      <header className="relative pt-40 pb-20 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/about#careers"
            className="inline-flex items-center gap-2 text-gray-500 text-xs uppercase tracking-widest hover:text-[#C4A77D] transition-colors mb-10"
          >
            <ArrowLeft size={14} /> All Roles
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="text-[10px] uppercase tracking-[0.25em] px-3 py-1 border border-[#C4A77D]/30 text-[#C4A77D]">
              {job.department}
            </span>
            <span className="flex items-center gap-1.5 text-gray-500 text-xs">
              <MapPin size={12} className="text-[#C4A77D]" /> {job.location}
            </span>
            <span className="flex items-center gap-1.5 text-gray-500 text-xs">
              <Clock size={12} className="text-[#C4A77D]" /> {job.type}
            </span>
          </div>

          <h1
            className="text-4xl md:text-6xl text-white font-light leading-tight mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {job.title}
          </h1>

          <a
            href={`mailto:careers@raha-iduu.com?subject=${applySubject}&body=${applyBody}`}
            className="inline-flex items-center gap-2 px-10 py-4 bg-[#C4A77D] border border-[#C4A77D] text-black text-xs uppercase tracking-wider hover:bg-white hover:border-white transition-all duration-300"
          >
            <Mail size={14} /> Apply for This Role
          </a>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-20 space-y-16">

        {/* About the role */}
        <section>
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">About the Role</span>
          <p className="mt-5 text-gray-400 text-base font-light leading-relaxed">{job.about}</p>
        </section>

        {/* Responsibilities */}
        <section>
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">What You'll Do</span>
          <ul className="mt-5 space-y-3">
            {job.responsibilities.map((r, i) => (
              <li key={i} className="flex items-start gap-4 text-gray-400 text-sm font-light leading-relaxed">
                <span className="w-1 h-1 bg-[#C4A77D] rounded-full flex-shrink-0 mt-2" />
                {r}
              </li>
            ))}
          </ul>
        </section>

        {/* Requirements */}
        <section>
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">What We're Looking For</span>
          <ul className="mt-5 space-y-3">
            {job.requirements.map((r, i) => (
              <li key={i} className="flex items-start gap-4 text-gray-400 text-sm font-light leading-relaxed">
                <span className="w-1 h-1 bg-[#C4A77D] rounded-full flex-shrink-0 mt-2" />
                {r}
              </li>
            ))}
          </ul>
        </section>

        {/* Benefits */}
        <section>
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">What We Offer</span>
          <ul className="mt-5 space-y-3">
            {job.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-4 text-gray-400 text-sm font-light leading-relaxed">
                <span className="w-1 h-1 bg-[#C4A77D] rounded-full flex-shrink-0 mt-2" />
                {b}
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="border border-[#C4A77D]/20 p-10 text-center">
          <p className="text-gray-400 text-sm font-light mb-2">Ready to join the team?</p>
          <p className="text-gray-500 text-xs mb-8 max-w-md mx-auto">
            Send your CV and a brief covering note to{" "}
            <span className="text-[#C4A77D]">careers@raha-iduu.com</span>. We review every application personally.
          </p>
          <a
            href={`mailto:careers@raha-iduu.com?subject=${applySubject}&body=${applyBody}`}
            className="inline-flex items-center gap-2 px-10 py-4 bg-[#C4A77D] border border-[#C4A77D] text-black text-xs uppercase tracking-wider hover:bg-white hover:border-white transition-all duration-300"
          >
            <Mail size={14} /> Apply Now
          </a>
        </section>

      </div>
    </main>
  );
}
